import React, { useMemo, useState } from "react";
import { Button, Card, Col, Row, Segmented, Tooltip } from "antd";
import { ExpandOutlined } from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import { CHART_COLORS, ExecutionMonthColumn, fmtMonth, num } from "./executionShared";
import {
  AXIS,
  CLICKABLE,
  LEGEND,
  MONEY_AXIS,
  TOOLTIP,
  seriesClickIndex,
} from "./executionChartKit";
import { MEASURE_FIELDS, OutflowMeasure } from "./ExecutionChartAnalysis";

// Dashboard chart cards for the Monthly Execution Summary — deliberately calm:
// the cards answer "are we on track?" in a glance (muted plan-side, one teal
// primary series, hairline grid, light axis ink, values on hover only);
// everything analytical (series toggles, cumulative view, month table,
// insights) lives behind the ⛶ expand control. Palette is the validated
// role-based set in CHART_COLORS — plan = soft blue, actual = teal,
// planned revenue = subtle dashed steel blue, backlog = thin amber exception line.
// One ₹ axis everywhere — no dual axes.
//
// The cards are also the drill entry point: clicking a month opens that card's
// analysis modal ALREADY drilled into it, so the gesture on the small chart and
// the gesture on the big one mean the same thing.

const cardHeadStyle: React.CSSProperties = {
  borderBottom: "none",
  minHeight: 44,
  padding: "0 16px",
};

// Quiet affordance for the drill gesture — the bars themselves already carry a
// pointer cursor, this just says so once per card.
const hintStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#a9b6c9",
  textAlign: "center",
  marginTop: -4,
};

const cardTitle = (text: string) => (
  <span style={{ fontSize: 13.5, fontWeight: 600, color: "#022c57" }}>{text}</span>
);

const expandButton = (onClick: () => void) => (
  <Tooltip title="Open detailed analysis">
    <Button type="text" size="small" icon={<ExpandOutlined style={{ color: "#8094ae" }} />} onClick={onClick} />
  </Tooltip>
);

const ExecutionCharts: React.FC<{
  months: ExecutionMonthColumn[];
  /** Opens the Analysis page: view tab, optional drilled month, optional 3M pillar. */
  onOpenAnalysis: (
    view: "target-executed" | "outflow",
    month?: string | null,
    measure?: OutflowMeasure
  ) => void;
}> = ({ months, onOpenAnalysis }) => {
  const [measure, setMeasure] = useState<OutflowMeasure>("total");

  // Clicking a month drills; the ⛶ control opens the same page undrilled.
  const chartClick = (
    view: "target-executed" | "outflow",
    pillar?: OutflowMeasure
  ) => ({
    click: (params: any) => {
      const index = seriesClickIndex(params);
      if (index !== null && months[index]) {
        onOpenAnalysis(view, months[index].month, pillar);
      }
    },
  });

  const labels = useMemo(() => months.map((m) => fmtMonth(m.month)), [months]);
  const mask = (m: ExecutionMonthColumn, value: number) => (m.isActualized ? value : null);
  // A month with an explicit 0.00 plan is a real planned-revenue entry, so test for a value
  // being present rather than for it being positive.
  const hasClientPlan = useMemo(
    () => months.some((m) => num(m.clientPlanAmount) > 0 || m.clientPlanIsDerived === false),
    [months]
  );
  const hasBacklog = useMemo(
    () => months.some((m) => m.isActualized && Math.abs(num(m.closingBacklog)) > 1),
    [months]
  );

  // ---- Chart 1: Planned (muted reference) vs Executed (primary) expenditure ----
  // Planned Revenue / Backlog stay visually secondary: thin lines, and omitted
  // entirely when they carry no signal (no plan entered / zero backlog) —
  // the expanded analysis always offers them as toggles.
  const cashOption = useMemo(() => {
    const series: any[] = [
      {
        name: "Planned Expenditure",
        type: "bar",
        data: months.map((m) => num(m.targetAmount)),
        itemStyle: { color: CHART_COLORS.plan, borderRadius: [3, 3, 0, 0], ...CLICKABLE },
        barMaxWidth: 26,
      },
      {
        name: "Executed Expenditure",
        type: "bar",
        data: months.map((m) => mask(m, num(m.executedAmount))),
        itemStyle: { color: CHART_COLORS.actual, borderRadius: [3, 3, 0, 0], ...CLICKABLE },
        barMaxWidth: 26,
      },
    ];
    if (hasClientPlan) {
      series.push({
        name: "Planned Revenue",
        type: "line",
        data: months.map((m) => num(m.clientPlanAmount) || null),
        itemStyle: { color: CHART_COLORS.clientPlan, ...CLICKABLE },
        lineStyle: { type: "dashed", width: 1.5, color: CHART_COLORS.clientPlan },
        symbol: "circle",
        symbolSize: 4,
      });
    }
    if (hasBacklog) {
      series.push({
        name: "Closing Backlog",
        type: "line",
        data: months.map((m) => mask(m, num(m.closingBacklog))),
        itemStyle: { color: CHART_COLORS.backlog, ...CLICKABLE },
        lineStyle: { width: 1.5, color: CHART_COLORS.backlog },
        symbol: "diamond",
        symbolSize: 6,
      });
    }
    return {
      tooltip: TOOLTIP,
      legend: LEGEND,
      grid: { left: 62, right: 16, top: 16, bottom: 44 },
      xAxis: { type: "category", data: labels, ...AXIS },
      yAxis: MONEY_AXIS,
      series,
    };
  }, [months, labels, hasClientPlan, hasBacklog]);

  // ---- Chart 2: Planned vs Earned for the selected measure (2 series) ----
  const fields = MEASURE_FIELDS[measure];
  const outflowOption = useMemo(
    () => ({
      tooltip: TOOLTIP,
      legend: LEGEND,
      grid: { left: 62, right: 16, top: 16, bottom: 44 },
      xAxis: { type: "category", data: labels, ...AXIS },
      yAxis: MONEY_AXIS,
      series: [
        {
          name: `${fields.label} Planned`,
          type: "bar",
          data: months.map((m) => num(m[fields.planned] as string)),
          itemStyle: { color: CHART_COLORS.plan, borderRadius: [3, 3, 0, 0], ...CLICKABLE },
          barMaxWidth: 26,
        },
        {
          name: `${fields.label} Earned`,
          type: "bar",
          data: months.map((m) => mask(m, num(m[fields.earned] as string))),
          itemStyle: { color: CHART_COLORS.actual, borderRadius: [3, 3, 0, 0], ...CLICKABLE },
          barMaxWidth: 26,
        },
      ],
    }),
    [months, labels, fields]
  );

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} xl={13}>
          <Card
            size="small"
            style={{ borderRadius: 12, height: "100%", borderColor: "#eef1f6" }}
            headStyle={cardHeadStyle}
            title={cardTitle("Planned vs Executed Expenditure — with Carry-over Backlog")}
            extra={expandButton(() => onOpenAnalysis("target-executed", null))}
          >
            <ReactECharts
              option={cashOption}
              style={{ height: 290 }}
              notMerge
              onEvents={chartClick("target-executed")}
            />
            <div style={hintStyle}>Click a month to drill in</div>
          </Card>
        </Col>
        <Col xs={24} xl={11}>
          <Card
            size="small"
            style={{ borderRadius: 12, height: "100%", borderColor: "#eef1f6" }}
            headStyle={cardHeadStyle}
            title={cardTitle("3M Expenditure — Planned vs Executed")}
            extra={
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Segmented
                  size="small"
                  options={(["total", "material", "manpower", "machinery"] as const).map(
                    (key) => ({ label: MEASURE_FIELDS[key].label, value: key })
                  )}
                  value={measure}
                  onChange={(value) => setMeasure(value as OutflowMeasure)}
                />
                {expandButton(() => onOpenAnalysis("outflow", null, measure))}
              </div>
            }
          >
            <ReactECharts
              option={outflowOption}
              style={{ height: 290 }}
              notMerge
              onEvents={chartClick("outflow", measure)}
            />
            <div style={hintStyle}>Click a month to drill in</div>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ExecutionCharts;
